import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {Button, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';

/**
 * Header / drawer tab row for Contentful primary items (Shop, Discover, Support).
 *
 * @param {Object} props
 * @param {Array<{id: string, title: string}>} props.tabs
 * @param {string|null} props.activeTabId
 * @param {function} props.onTabClick
 */
export const NavigationPrimaryTabs = ({
  tabs = [],
  activeTabId,
  onTabClick,
  tone,
  ...props
}) => {
  const intl = useIntl();
  const {headerStyle} = useHeaderContext();
  const styles = useMultiStyleConfig('MegaMenu', {
    variant: tone === 'drawer' ? 'drawer' : headerStyle,
  });

  if (!tabs.length) {
    return null;
  }

  return (
    <Flex
      as="nav"
      aria-label={intl.formatMessage({
        defaultMessage: 'Primary navigation',
        id: 'header.nav.primary_aria',
      })}
      className="header-primary-nav"
      {...styles.primaryTabs}
      {...props}
    >
      {tabs.map((tab) => {
        const isActive = activeTabId === tab.id;

        return (
          <Button
            key={tab.id}
            aria-expanded={isActive}
            aria-haspopup="dialog"
            className={`header-primary-nav__item${isActive ? ' header-primary-nav__item--active' : ''}`}
            variant="unstyled"
            {...styles.primaryTab}
            {...(isActive ? styles.primaryTabActive : (activeTabId ? styles.primaryTabInactive : {}))}
            onClick={() => onTabClick(tab.id)}
          >
            {tab.title}
          </Button>
        );
      })}
    </Flex>
  );
};

NavigationPrimaryTabs.displayName = 'NavigationPrimaryTabs';
NavigationPrimaryTabs.propTypes = {
  tabs: PropTypes.array,
  activeTabId: PropTypes.string,
  onTabClick: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(['drawer']),
};
